<template>
    <SimplePlane
        :material="material"
        :layer="layer"
        :renderOrder="renderOrder"
        :visible="visible"
    />
</template>

<script setup>
    import { useThreeOrthoObject } from '#imports';
    import { computed, ref, watch, watchEffect } from 'vue';

    import { usePane, useRafBool } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import { borderGlowMaterial } from './border-glow-material';
    import SimplePlane from '~/components/gl/common/SimplePlane.vue';
    import { createPulseGradientRefs } from '~/components/gl/shaders/ShaderChunks/radial-pulse-gradient';
    import { LAYER_BG } from '~/core/constants';
    import { useColorPalette } from '~/providers/ColorPaletteProvider';

    const props = defineProps({
        color: { type: String, default: '#7dd2ff' },
        layer: { type: Number, default: LAYER_BG },
        renderOrder: { type: Number, default: 2 },
        visible: { type: Boolean, default: true },
        active: { type: Boolean, default: true },
    });

    const BASE_BRIGHTNESS = 0.2;
    const BASE_ROTATE_SPEED = 0.15;
    const BASE_BLUR_LEVEL = 0.25;

    const active = computed(() => props.active);

    useThreeOrthoObject(null, { name: 'BorderGlow' });

    // Tweakable refs — also driven by idle/momentous methods
    const color = ref(props.color);
    const visible = ref(props.visible);

    const brightness = ref(0);
    const rotateSpeed = ref(0);
    const scale = ref(1.2);
    const blurLevel = ref(BASE_BLUR_LEVEL);
    const cornerThreshold = ref(-0.23);
    /** Radians: full width of the glowing wedge; ~6.28 = full circle. */
    const coneSpread = ref(6.27);
    /** 0 = sharp wedge edge, 1 = soft gradient across half-spread. */
    const coneFalloff = ref(0.7);

    const material = borderGlowMaterial({
        coneSpread: coneSpread.value,
        coneFalloff: coneFalloff.value,
    });

    const { uPulseProgress, uPulseThickness, uPulseFalloff, uPulseMaxReach } =
        createPulseGradientRefs('BorderGlow Pulse');
    uPulseFalloff.value = 0.3;

    const { colorPalette } = useColorPalette();

    material.uniforms.uColor.value = colorPalette[0];
    // Additive blending so it adds on top of whatever is behind
    // material.blending = AdditiveBlending;
    // material.transparent = true;

    let tl = null;

    useRafBool(active, ({ delta }) => {
        material.uniforms.uTime.value += delta;
        material.uniforms.uRotateSpeed.value += delta * 0.001 * rotateSpeed.value;
    });

    watchEffect(() => {
        // material.uniforms.uColor.value = new Color(color.value);
        material.uniforms.uBrightness.value = brightness.value;
        material.uniforms.uCornerThreshold.value = cornerThreshold.value;
        material.uniforms.uScale.value = scale.value;
        material.uniforms.uBlurLevel.value = blurLevel.value;
        material.uniforms.uConeSpread.value = coneSpread.value;
        material.uniforms.uConeFalloff.value = coneFalloff.value;
    });

    watchEffect(() => {
        material.uniforms.uPulseProgress.value = uPulseProgress.value;
        material.uniforms.uPulseThickness.value = uPulseThickness.value;
        material.uniforms.uPulseFalloff.value = uPulseFalloff.value;
        material.uniforms.uPulseMaxReach.value = uPulseMaxReach.value;
    });

    const pulse = () => {
        const tl = gsap.timeline();
        tl.fromTo(uPulseProgress, { value: 0 }, { value: 1, duration: 2.2, ease: 'power2.inOut' });

        return tl;
    };

    const show = ({
        brightness: brightnessBase = BASE_BRIGHTNESS,
        rotateSpeed: rotateSpeedBase = BASE_ROTATE_SPEED,
        blurLevel: blurLevelBase = BASE_BLUR_LEVEL,
        riseDuration = 1,
        fallDuration = 1,
        easeRise = 'sine.out',
        easeFall = 'sine.inOut',
        addPulse = true,
        delay = 0,
        pulseDelay = 0,
        outStart = 2,
    } = {}) => {
        tl?.kill();
        tl = gsap.timeline({ delay });

        // Fade in
        tl.to(brightness, { value: brightnessBase, duration: riseDuration, ease: easeRise }, 0);
        tl.to(rotateSpeed, { value: rotateSpeedBase, duration: riseDuration, ease: easeRise }, 0);
        tl.to(blurLevel, { value: blurLevelBase, duration: riseDuration, ease: easeRise }, 0);

        // Fade out
        tl.to(brightness, { value: 0, duration: fallDuration, ease: easeFall }, outStart);
        tl.to(
            rotateSpeed,
            { value: BASE_ROTATE_SPEED, duration: fallDuration, ease: easeFall },
            outStart
        );
        tl.to(
            blurLevel,
            { value: BASE_BLUR_LEVEL, duration: fallDuration, ease: easeFall },
            outStart
        );

        if (addPulse) tl.add(pulse(), pulseDelay);
    };

    const fadeIn = ({
        brightness: brightnessTarget = BASE_BRIGHTNESS,
        rotateSpeed: rotateSpeedTarget = BASE_ROTATE_SPEED,
        blurLevel: blurLevelTarget = BASE_BLUR_LEVEL,
        duration = 0.8,
        ease = 'sine.out',
        delay = 0,
    } = {}) => {
        tl?.kill();
        tl = gsap.timeline({ delay });
        tl.to(brightness, { value: brightnessTarget, duration, ease }, 0);
        tl.to(rotateSpeed, { value: rotateSpeedTarget, duration, ease }, 0);
        tl.to(blurLevel, { value: blurLevelTarget, duration, ease }, 0);
        return tl;
    };

    const hide = ({ duration = 1, ease = 'sine.inOut', onComplete } = {}) => {
        tl?.kill();

        tl = gsap.timeline({ onComplete });
        tl.to(brightness, { value: 0, duration, ease });
        tl.to(rotateSpeed, { value: 0, duration, ease });
    };

    watch(
        () => props.visible,
        (v) => (visible.value = v)
    );

    watch(
        () => props.color,
        (c) => {
            if (c) color.value = c;
        }
    );

    defineExpose({ pulse, show, hide, fadeIn });

    // usePane(
    //     [
    //         { name: 'visible', value: visible },
    //         { name: 'scale', value: scale, options: { min: 0, max: 2, step: 0.01 } },
    //         { name: 'color', value: color },
    //         { name: 'brightness', value: brightness, options: { min: 0, max: 3, step: 0.01 } },
    //         { name: 'rotateSpeed', value: rotateSpeed, options: { min: 0, max: 1, step: 0.001 } },
    //         {
    //             name: 'cornerThreshold',
    //             value: cornerThreshold,
    //             options: { min: -1, max: 1, step: 0.01 },
    //         },
    //         { name: 'blurLevel', value: blurLevel, options: { min: 0, max: 1, step: 0.01 } },
    //         {
    //             name: 'coneSpread',
    //             value: coneSpread,
    //             options: { min: 0.1, max: Math.PI * 2, step: 0.01 },
    //         },
    //         { name: 'coneFalloff', value: coneFalloff, options: { min: 0, max: 1, step: 0.01 } },
    //     ],
    //     { title: 'BorderGlow', expanded: false }
    // );
</script>
